import {FC, useState, useEffect} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Button} from 'react-bootstrap'
import {useParams, useSearchParams} from 'react-router-dom'
import {ListComodity} from './components/ListComodity'
import {
  fetchDataTradeFlow,
  getDetailCountry,
  getDetailTradeFlow,
  importDataManual,
  getComtradeJobStatus,
  getLastSyncJob,
  revertComtradeJob,
} from './core/_requests'

import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {useSocket} from '../../../contexts/SocketContext'
import {CountryType, ListComodityResultRowType} from './core/_models'
import ModalImportData from './components/ModalImportData'
import './TradeDataManagementDetailPage.scss'
import {getNameByISO3} from '../../../libs/misc'
import {showSwal} from '../../../../ui-kit'

const TradeDataManagementDetailPage: FC = () => {
  const axiosPrivate = useAxiosPrivate()
  const {socket, connected} = useSocket()
  const [searchParams, setSearchParams] = useSearchParams()

  const [isSyncing, setIsSyncing] = useState(false)
  const [syncProgress, setSyncProgress] = useState(0)
  const [syncMessage, setSyncMessage] = useState('')

  const [showResultSection, setShowResultSection] = useState(false)
  const [showNoResultSection, setShowNoResultSection] = useState(false)
  const [availabilityComtrade, setAvailabilityComtrade] = useState(false)

  // Tracks what /list-trade-flows actually came back with for this
  // specific year+HS combo — used to tell "genuinely nothing available
  // anywhere (not on Comtrade, no mirror data)" apart from "showing real
  // mirrored data", instead of assuming mirror data exists just because
  // the country-level Comtrade file_url check came back empty.
  const [tradeFlowMeta, setTradeFlowMeta] = useState<{isMirror: boolean; count: number} | null>(
    null
  )
  const [logNotFound, setLogNotFound] = useState(false)

  const [showImportModal, setShowImportModal] = useState(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importLoading, setImportLoading] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)

  const {countrycode} = useParams()
  const [year, setYear] = useState<string>(searchParams.get('year') || '2023')
  const [hsCode, setHsCode] = useState<string>(searchParams.get('hs_vr') || 'H4')
  const [sourceType, setSourceType] = useState<string>(
    searchParams.get('source_type') || 'comtrade'
  )

  const [detailCountry, setDetailCountry] = useState<CountryType>({
    rep: '',
    name: '',
    repcode: '',
    record_size: 0,
  })

  const [listComodityTableData, setListComodityTableData] =
    useState<Array<ListComodityResultRowType> | null>()

  const [countData, setCountData] = useState(0)
  const [isLoadingComodity, setIsLoadingComodity] = useState(true)

  const [lastSyncJob, setLastSyncJob] = useState<{
    id: string | number
    isReverted: boolean
    timestamp: number
  } | null>(null)
  const [isReverting, setIsReverting] = useState(false)

  const fetchLastSyncStatus = async () => {
    if (!countrycode || sourceType !== 'comtrade') return
    try {
      const res = await getLastSyncJob(axiosPrivate, {
        rep: countrycode,
        year,
        hs_vr: hsCode,
      })
      setLastSyncJob(res.data.job)
    } catch (err) {
      console.error('Failed to fetch last sync job status:', err)
    }
  }

  const handleRevertSync = async () => {
    if (!lastSyncJob) return

    showSwal({
      title: 'Are you sure you want to revert this synchronization?',
      text: 'This will delete the current synchronized data and restore the previous trade flow records from the backup.',
      icon: 'warning',
      confirmButtonText: 'Yes, revert it',
      cancelButtonText: 'No, keep it',
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsReverting(true)
        try {
          await revertComtradeJob(axiosPrivate, lastSyncJob.id)
          showSwal({
            title: 'Reverted!',
            text: 'Synchronization reverted successfully and previous records have been restored.',
            icon: 'success',
            confirmButtonText: 'OK',
          }).then(() => {
            window.location.reload()
          })
        } catch (err: any) {
          console.error('Revert error:', err)
          const errMsg = err.response?.data?.message || 'Failed to revert the synchronization.'
          showSwal({
            title: 'Error',
            text: errMsg,
            icon: 'error',
          })
        } finally {
          setIsReverting(false)
        }
      }
    })
  }

  useEffect(() => {
    setIsLoadingComodity(true)
    const runEffect = async () => {
      if (!countrycode) return

      setDetailCountry((prev) => ({
        ...prev,
        rep: countrycode,
        name: getNameByISO3(countrycode),
      }))

      // 1. Ensure detailCountry is fetched first
      try {
        const res = await getDetailCountry(axiosPrivate, {rep: countrycode})
        const resData = res.data.data

        setShowResultSection(true)
        if (resData.record_size === 0) {
          setDetailCountry((prev) => ({
            ...prev,
            record_size: resData.record_size,
          }))

          setShowNoResultSection(true)
          setAvailabilityComtrade(resData.file_url)
        }
      } catch (err) {
        console.error('Failed to load country details:', err)
        return
      }

      const tradeFlowPromise = getDetailTradeFlow(axiosPrivate, {
        code: hsCode,
        rep: countrycode,
        year,
        source_type: sourceType,
      })

      try {
        const resDetail = await tradeFlowPromise

        setTradeFlowMeta({
          isMirror: !!resDetail.data.is_mirror,
          count: resDetail.data.count ?? resDetail.data.table_data.length,
        })

        if (resDetail.data.table_data.length > 0) {
          setListComodityTableData(resDetail.data.table_data)
          setCountData(resDetail.data.count)
        } else {
          setListComodityTableData([])
        }
      } catch (err: any) {
        if (err.response?.status === 404) {
          // No sync log at all for this rep/year/HS — genuinely never
          // synced or attempted, same "nothing available" case as a
          // mirror lookup that came back empty (see noDataAvailable below).
          setLogNotFound(true)
          setListComodityTableData([])
        } else {
          showSwal({
            title: 'Error!',
            html: `${err?.response?.data?.message || 'An unexpected error occurred'}`,
            icon: 'error',
            showCloseButton: true,
            showCancelButton: true,
            showConfirmButton: false,
            cancelButtonText: 'Close',
          })
        }
      } finally {
        setIsLoadingComodity(false)
      }
    }

    runEffect()
    fetchLastSyncStatus()
  }, [])

  // handle sync — fire-and-forget, no blocking overlay
  const handleSync = () => {
    showSwal({
      title: 'Synchronizing…',
      text: 'The synchronization has been queued and is running in the background. You can continue using the page.',
      icon: 'info',
      confirmButtonText: 'OK',
    })

    fetchDataTradeFlow(axiosPrivate, {
      rep: detailCountry.rep,
      year: year,
      hs_vr: hsCode,
    })
      .then((res) => {
        if (res.status === 202) {
          const {jobId} = res.data
          setIsSyncing(true)
          setSyncProgress(0)
          setSyncMessage('Synchronizing…')

          if (socket && connected) {
            const roomName = `job-${jobId}`
            socket.emit('join-room', roomName)

            const handleProgress = (data: any) => {
              if (data.percent !== undefined) {
                setSyncProgress(data.percent)
              }
              if (data.message) {
                setSyncMessage(data.message)
              }

              if (data.status === 'completed') {
                socket.emit('leave-room', roomName)
                socket.off('progress', handleProgress)
                setIsSyncing(false)
                showSwal({
                  title: 'Synchronization Complete',
                  text: 'Trade data has been synchronized successfully. Reload the page to see the latest data.',
                  icon: 'success',
                  confirmButtonText: 'Reload',
                  showCancelButton: true,
                  cancelButtonText: 'Later',
                }).then((result) => {
                  if (result.isConfirmed) window.location.reload()
                })
              } else if (data.status === 'failed') {
                socket.emit('leave-room', roomName)
                socket.off('progress', handleProgress)
                setIsSyncing(false)
                showSwal({
                  title: 'Synchronization Failed',
                  text: data.failedReason || 'The background job failed.',
                  icon: 'error',
                })
              }
            }

            socket.on('progress', handleProgress)
          } else {
            // Fallback to polling
            const intervalId = setInterval(async () => {
              try {
                const statusRes = await getComtradeJobStatus(axiosPrivate, jobId)
                const {state, progress, failedReason} = statusRes.data

                if (progress) {
                  setSyncProgress(progress.percent || 0)
                  if (progress.message) setSyncMessage(progress.message)
                }

                if (state === 'completed') {
                  clearInterval(intervalId)
                  setIsSyncing(false)
                  showSwal({
                    title: 'Synchronization Complete',
                    text: 'Trade data has been synchronized successfully. Reload the page to see the latest data.',
                    icon: 'success',
                    confirmButtonText: 'Reload',
                    showCancelButton: true,
                    cancelButtonText: 'Later',
                  }).then((result) => {
                    if (result.isConfirmed) window.location.reload()
                  })
                } else if (state === 'failed') {
                  clearInterval(intervalId)
                  setIsSyncing(false)
                  showSwal({
                    title: 'Synchronization Failed',
                    text: failedReason || 'The background job failed.',
                    icon: 'error',
                  })
                }
              } catch (err: any) {
                console.error('Error polling job status:', err)
              }
            }, 2000)
          }
        }
      })
      .catch((e) => {
        const errMsg = e.response?.data?.message || 'Failed to start synchronization.'
        showSwal({title: 'Error', text: errMsg, icon: 'error'})
      })
  }

  // handle import excel
  const handleImportSubmit = async () => {
    if (!importFile || !detailCountry.rep || !year) return

    const MAX_FILE_SIZE_MB = 5
    const maxFileSize = MAX_FILE_SIZE_MB * 1024 * 1024

    if (importFile.size > maxFileSize) {
      setImportError(`File size exceeds ${MAX_FILE_SIZE_MB}MB. Please upload a smaller file.`)
      return
    }

    setImportLoading(true)
    setImportError(null)

    try {
      const formData = new FormData()
      formData.append('rep', detailCountry.rep)
      formData.append('file', importFile)
      formData.append('year', year)
      formData.append('hs_vr', hsCode)

      importDataManual(axiosPrivate, formData)
        .then((res) => {
          if (res.status === 200) {
            setShowImportModal(false)
            showSwal({
              title: 'Import Successful',
              icon: 'success',
              confirmButtonText: 'OK',
            }).then(() => {
              window.location.reload()
            })
          }
        })
        .catch((resErr: any) => {
          const message =
            resErr?.response?.data?.message || 'Upload failed. Please check your file.'
          setImportError(message)
        })
        .finally(() => {
          setImportLoading(false)
        })
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Upload failed. Please check your file.'
      setImportError(message)
    }
  }

  // Genuinely nothing available for this year+HS — not on Comtrade, and no
  // mirror (partner-reported) data either. This used to fall through to
  // either a crash (empty data-extraction-log row) or a misleading "we're
  // showing mirrored data" banner over an empty table. Checked first, ahead
  // of the other two banners below, since it overrides them.
  const noDataAvailable =
    sourceType === 'comtrade' &&
    (logNotFound || (tradeFlowMeta?.isMirror === true && tradeFlowMeta.count === 0))

  const renderStatusBanner = () => {
    if (noDataAvailable) {
      return (
        <div className='alert-no-result-data'>
          <div className='alert alert-secondary d-flex align-items-center p-5'>
            <div className='alert-content d-flex align-items-center'>
              <span className='svg-icon svg-icon-2hx svg-icon-secondary me-3'>
                <i className='fa-solid fa-triangle-exclamation'></i>
              </span>
              <div className='d-flex flex-column'>
                <span>
                  Unfortunately, we couldn't find any export mirrored data of{' '}
                  {detailCountry.name}.
                </span>
              </div>
            </div>
          </div>
        </div>
      )
    }

    if (showNoResultSection && sourceType === 'comtrade') {
      if (availabilityComtrade) {
        return (
          <div className='alert-no-result-data'>
            <div className='alert alert-danger d-flex align-items-center justify-content-between p-5'>
              <div className='alert-content d-flex align-items-center'>
                <span className='svg-icon svg-icon-2hx svg-icon-danger me-3'>
                  <i className='fa-solid fa-triangle-exclamation'></i>
                </span>
                <div className='d-flex flex-column'>
                  <h5 className='mb-1'>Data Not Found</h5>
                  <span>
                    Unfortunately, we couldn't find any export data of {detailCountry.name} trade
                    flow. Please synchronize it now.
                  </span>
                </div>
              </div>
              <Button className='ms-2' variant='primary' size='sm' onClick={handleSync}>
                Synchronize Now
              </Button>
            </div>
          </div>
        )
      }

      if (tradeFlowMeta?.isMirror && tradeFlowMeta.count > 0) {
        return (
          <div className='alert-no-result-data'>
            <div className='alert alert-warning d-flex align-items-center justify-content-between p-5'>
              <div className='alert-content d-flex align-items-center'>
                <span className='svg-icon svg-icon-2hx svg-icon-warning me-3'>
                  <i className='fa-solid fa-triangle-exclamation'></i>
                </span>
                <div className='d-flex flex-column'>
                  <span>
                    Unfortunately, data of {detailCountry.name} {year} trade flow is not available
                    at Comtrade. We're showing <strong>mirrored data</strong> based on reports from
                    partner countries stored in our database
                  </span>
                </div>
              </div>
            </div>
          </div>
        )
      }
    }

    return null
  }

  return (
    <>
      {isSyncing && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 9999,
            background: '#1e1e2d',
            color: '#fff',
            borderRadius: '12px',
            padding: '16px 20px',
            minWidth: '280px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
            <span
              className='spinner-border spinner-border-sm text-primary'
              role='status'
              aria-hidden='true'
            />
            <span style={{fontWeight: 600, fontSize: '13px'}}>Syncing in Background</span>
          </div>
          <p style={{margin: 0, fontSize: '12px', color: '#9899ac'}}>{syncMessage}</p>
          <div className='progress h-6px w-100 bg-secondary'>
            <div
              className='progress-bar progress-bar-striped progress-bar-animated bg-primary'
              style={{width: `${syncProgress}%`}}
            />
          </div>
          <span style={{fontSize: '11px', color: '#9899ac'}}>{syncProgress}% complete</span>
        </div>
      )}
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: 'Trade Data Management',
            path: 'trade-data',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        Detail
      </PageTitle>
      <>
        <div className='TradeDataManagementDetailPage'>
          <Row className='mb-5'>
            <Col>
              <Card>
                <Card.Header className='card-header-stretch'>
                  <Card.Title className='w-100'>
                    <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                      <div className='title d-flex align-items-center'>
                        <h3 className='text-gray-700 m-0'>{detailCountry.name}</h3>
                      </div>
                      {lastSyncJob && !lastSyncJob.isReverted && (
                        <div className='d-flex gap-2'>
                          <Button
                            variant='outline-danger'
                            size='sm'
                            className='d-flex align-items-center gap-2 text-hover-white'
                            onClick={handleRevertSync}
                            disabled={isReverting || isLoadingComodity}
                          >
                            <i className='fa-solid fa-undo'></i>
                            {isReverting ? 'Reverting...' : 'Revert Latest Sync'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  {/* <Row className='mb-5'>
                  <Col>
                    <TradeDataDropZone />
                  </Col>
                </Row> */}
                  {showResultSection ? (
                    <>
                      {renderStatusBanner()}
                      <ListComodity
                        rows={listComodityTableData}
                        counts={listComodityTableData?.length ?? 0}
                        isLoading={isLoadingComodity}
                        sourceType={sourceType}
                        onImportClick={() => setShowImportModal(true)}
                      />
                    </>
                  ) : (
                    ''
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </>
      <ModalImportData
        show={showImportModal}
        onHide={() => {
          setImportError(null)
          setShowImportModal(false)
          setImportLoading(false)
        }}
        onSubmit={handleImportSubmit}
        onFileChange={(file) => setImportFile(file)}
        isLoading={importLoading}
        error={importError}
      />
    </>
  )
}

export default TradeDataManagementDetailPage
